/**
 * Patch-notes content as it arrives from the API. Descriptions are already resolved to a
 * single language server-side (requested language -> user culture -> corporation default -> en),
 * so nothing here does language fallback. The requested language is the one the app is
 * currently rendering in, sent on every fetch; see `currentLanguage` below for why the reply
 * has to be re-fetched when it changes.
 */
export interface PatchNoteEntry {
    /**
     * Untranslated literal code: "New" | "Improved" | "Fixed". Map it to a label with
     * t('patchNotes.category.*') — rendering this value directly ships English labels to
     * every locale. See CategoryBadge.
     */
    category: string,
    description: string,
    /**
     * Optional product area ("Payroll", "Time Tracking"), rendered as a chip before the text.
     * Absent on releases authored before the field existed, and on entries that span no single
     * module — ModuleChip renders nothing in that case.
     *
     * Unlike `category` this is not a code: it is the English module name, and the app's locale
     * files are already keyed by exactly that string ("Payroll": "Gehaltsabrechnung"), so
     * t(module) resolves it with no new keys. See ModuleChip.
     */
    module?: string,
}

export interface PatchNoteRelease {
    /** Zero-padded "yyyy.MM.dd". */
    version: string,
    releaseDate: string,
    entries: PatchNoteEntry[],
}

export interface MarkPatchNotesAsSeenResponse {
    /** The version the server actually recorded; may differ from what was requested. */
    seenVersion: string | null,
}

export interface PatchNotesSliceModel {
    /** Latest published release, for the "What's New" modal. */
    current: PatchNoteRelease | null,
    /** Full published history, newest first, for the Patch Notes page. */
    all: PatchNoteRelease[],
    /**
     * The language `current` was fetched in.
     *
     * Descriptions are resolved to one language server-side, so cached content is only valid
     * for the language it was asked for: switching the application language has to refetch, not
     * just re-render, and the modal compares this against the live i18n language to decide.
     * `all` needs no equivalent: the history page refetches on every mount.
     *
     * Null before the first fetch. This slice is persisted to sessionStorage (store.ts), so
     * state saved before this field existed rehydrates as undefined - which compares unequal to
     * any language and therefore refetches, which is the right outcome.
     *
     * Set on a failed fetch too, so a failing endpoint cannot make the refetch effect spin.
     */
    currentLanguage: string | null,
    isLoadingCurrent: boolean,
    isLoadingAll: boolean,
    /** Set when the current-release fetch fails, so the modal can offer a retry. */
    currentError: string,
    allError: string,
    /** Whether the modal is on screen right now. */
    modalOpen: boolean,
    /**
     * Set once the user has closed the modal in this session, by any route. Drives the floating
     * reopen button, and stops the modal reopening on remount.
     */
    dismissedThisSession: boolean,
    /**
     * Set only when the user closed the modal via a confirming action ("Got it", or leaving for
     * the full history) rather than by setting it aside with X / backdrop. Suppresses the
     * floating reopen button: a "What's New" pill that appears the instant the user says they
     * read it reads as an unread badge that will not clear.
     *
     * Distinct from the persisted acknowledgement (MarkPatchNotesAsSeen), which happens on
     * every close path — this flag is session-scoped presentation only.
     *
     * Cleared by openWhatsNewModal, so each reopen gets to decide again. Without that reset it
     * would stick for the whole browser session: this slice rides the sessionStorage persist
     * config in store.ts, so it survives reloads too.
     */
    acknowledged: boolean,
}

export const patchNotesInitialValue: PatchNotesSliceModel = {
    current: null,
    all: [],
    currentLanguage: null,
    isLoadingCurrent: false,
    isLoadingAll: false,
    currentError: '',
    allError: '',
    modalOpen: false,
    dismissedThisSession: false,
    acknowledged: false,
};
